import { useMemo } from "react";
import {
  Box,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DuplicateOfTag from "../DuplicateOfTag";
import FetcherTablePagination from "../FetcherTablePagination";
import { SortDirection, sortRows } from "../helpers/tableSorting";

export interface ConfigTableRow {
  id: string;
  name: string;
  interfaceName: string;
  recipe: string;
  used: number;
  twin: string[];
  modified: string;
}

interface Props {
  rows: ConfigTableRow[];
  sortKey: string;
  sortDirection: SortDirection;
  onSort: (key: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  canEdit: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onShowBrands: (id: string) => void;
  onOpenTwin: (id: string) => void;
}

const headCells: { key: string; label: string; sortable: boolean }[] = [
  { key: "id", label: "Config ID", sortable: true },
  { key: "name", label: "Configuration Name", sortable: true },
  { key: "interfaceName", label: "Interface", sortable: true },
  { key: "recipe", label: "Recipe", sortable: false },
  { key: "used", label: "Used By", sortable: true },
  { key: "actions", label: "Quick Actions", sortable: false },
];

const ConfigsTable = ({
  rows,
  sortKey,
  sortDirection,
  onSort,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  canEdit,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onShowBrands,
  onOpenTwin,
}: Props) => {
  const sorted = useMemo(
    () => sortRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection],
  );
  const pageRows = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <>
      <TableContainer>
        <Table size="small" stickyHeader sx={{ whiteSpace: "nowrap" }}>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.key}
                  align={cell.key === "actions" ? "right" : "left"}
                >
                  {cell.sortable ? (
                    <TableSortLabel
                      active={sortKey === cell.key}
                      direction={sortKey === cell.key ? sortDirection : "asc"}
                      onClick={() => onSort(cell.key)}
                    >
                      {cell.label}
                    </TableSortLabel>
                  ) : (
                    cell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "#00000099", py: 3 }}
                  >
                    No configs match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                className="fetcher-row-link"
                onClick={() => onView(row.id)}
                data-tracker="1104"
              >
                <TableCell className="fetcher-mono">{row.id}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                    <DuplicateOfTag
                      ids={row.twin}
                      isDead={row.used === 0}
                      title={
                        row.used === 0
                          ? "This copy has no brands - safe to delete"
                          : "This one is live; the twin is the copy"
                      }
                      onClick={() => onOpenTwin(row.twin[0])}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      backgroundColor: "secondary.main",
                      color: "secondary.contrastText",
                      borderRadius: "14px",
                      padding: "3px 11px",
                      fontSize: "11.5px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.interfaceName}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: "12.5px", color: "#00000099" }}>
                    {row.recipe}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.used > 0 ? (
                    <Link
                      component="button"
                      underline="always"
                      color="secondary"
                      data-tracker="1103"
                      onClick={(event) => {
                        event.stopPropagation();
                        onShowBrands(row.id);
                      }}
                    >
                      {row.used} brands
                    </Link>
                  ) : (
                    <Typography sx={{ fontSize: "13px", color: "#00000099" }}>
                      0 brands
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Tooltip title={canEdit ? "Edit" : "View"}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(row.id)}
                        disabled
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <>
                        <Tooltip title="Duplicate">
                          <IconButton
                            size="small"
                            onClick={() => onDuplicate(row.id)}
                            disabled
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => onDelete(row.id)}
                            disabled
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FetcherTablePagination
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
};

export default ConfigsTable;
